import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { AdminPageHeader } from './components/AdminMasterDetail';
import { useAdminApi } from './hooks/useAdminApi';

const FREQUENCIES = ['monthly', 'quarterly', 'annual', 'full', '2-pay', '3-pay'];

interface Course {
  id: number;
  name: string;
  organizationId?: number;
  isActive?: boolean;
}

interface PriceEntry {
  id?: number;
  frequency: string;
  amount: number;
  currency: string;
  stripePriceId: string;
  stripeProductId: string;
  tierIncrement: number;
  isActive: boolean;
}

const buildEmptyEntry = (frequency: string): PriceEntry => ({
  frequency,
  amount: 0,
  currency: 'usd',
  stripePriceId: '',
  stripeProductId: '',
  tierIncrement: 1,
  isActive: true,
});

const ManageCoursePricing: React.FC = () => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [entries, setEntries] = useState<Record<string, PriceEntry>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const API_BASE_URL = `${config.apiBaseUrl}/api`;

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === selectedCourseId) ?? courses[0] ?? null,
    [courses, selectedCourseId]
  );

  const loadData = async () => {
    if (!hasActiveOrganization) {
      setError('Select an organization before managing course pricing.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);

      const [coursesResponse, pricingResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/courses`, { headers }),
        axios.get(`${API_BASE_URL}/organization-course-pricing${selectedCourseId ? `?courseId=${selectedCourseId}` : ''}`, { headers }),
      ]);

      const courseList: Course[] = Array.isArray(coursesResponse.data) ? coursesResponse.data : [];
      setCourses(courseList);
      const startingCourseId = courseList[0]?.id ?? null;
      setSelectedCourseId((current) => current ?? startingCourseId);

      const priceMap: Record<string, PriceEntry> = {};
      const pricingRows: Array<any> = Array.isArray(pricingResponse.data) ? pricingResponse.data : [];

      for (const row of pricingRows) {
        if (!row || !row.frequency) continue;
        priceMap[row.frequency] = {
          id: row.id,
          frequency: row.frequency,
          amount: Number(row.amount ?? 0),
          currency: row.currency ?? 'usd',
          stripePriceId: row.stripePriceId ?? '',
          stripeProductId: row.stripeProductId ?? '',
          tierIncrement: Number(row.tierIncrement ?? 1),
          isActive: row.isActive !== false,
        };
      }

      const nextEntries: Record<string, PriceEntry> = {};
      for (const frequency of FREQUENCIES) {
        nextEntries[frequency] = priceMap[frequency] ?? buildEmptyEntry(frequency);
      }

      setEntries(nextEntries);
    } catch (loadError: any) {
      setError(loadError?.response?.data?.error ?? loadError?.message ?? 'Failed to load course pricing data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [hasActiveOrganization, selectedCourseId]);

  useEffect(() => {
    if (!selectedCourseId && courses.length > 0) {
      setSelectedCourseId(courses[0].id);
    }
  }, [courses, selectedCourseId]);

  const handleFieldChange = (frequency: string, field: keyof PriceEntry, value: string | number | boolean) => {
    setEntries((current) => ({
      ...current,
      [frequency]: {
        ...current[frequency],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedCourseId) {
      setError('Select a course before saving.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setMessage(null);

      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);

      const prices = Object.values(entries)
        .filter((entry) => entry && typeof entry.frequency === 'string')
        .map((entry) => ({
          frequency: entry.frequency,
          amount: Number(entry.amount ?? 0),
          currency: entry.currency ?? 'usd',
          stripePriceId: entry.stripePriceId ?? '',
          stripeProductId: entry.stripeProductId ?? '',
          tierIncrement: Number(entry.tierIncrement ?? 1),
          isActive: entry.isActive !== false,
        }));

      await axios.post(
        `${API_BASE_URL}/organization-course-pricing/upsert`,
        { courseId: selectedCourseId, prices },
        { headers }
      );

      setMessage('Course pricing saved successfully. New purchases will use the organization-specific values for this course.');
      await loadData();
    } catch (saveError: any) {
      setError(saveError?.response?.data?.error ?? saveError?.message ?? 'Failed to save pricing changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px 20px 40px' }}>
      <AdminPageHeader
        title="Course Pricing"
        subtitle="Override Stripe pricing for specific courses within the active organization."
      />

      {!hasActiveOrganization && !loading && (
        <div style={{ background: '#fff5f5', border: '1px solid #fbcaca', borderRadius: 8, padding: 16, color: '#7f1d1d' }}>
          Select an organization to manage pricing.
        </div>
      )}

      {error && (
        <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: 12, marginBottom: 16, color: '#9f1239' }}>
          {error}
        </div>
      )}

      {message && (
        <div style={{ background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, marginBottom: 16, color: '#166534' }}>
          {message}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '32px 0', color: '#475569' }}>Loading pricing…</div>
      ) : (
        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            <label style={{ fontWeight: 600, color: '#1f2937' }}>Course</label>
            <select
              value={selectedCourseId ?? ''}
              onChange={(event) => setSelectedCourseId(Number(event.target.value) || null)}
              style={{ minWidth: 260, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
            >
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>

          {selectedCourse && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                <strong>{selectedCourse.name}</strong>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                      <th style={cellStyle}>Frequency</th>
                      <th style={cellStyle}>Amount (cents)</th>
                      <th style={cellStyle}>Currency</th>
                      <th style={cellStyle}>Stripe Price ID</th>
                      <th style={cellStyle}>Tier Increment</th>
                      <th style={cellStyle}>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FREQUENCIES.map((frequency) => {
                      const entry = entries[frequency] ?? buildEmptyEntry(frequency);

                      return (
                        <tr key={frequency} style={{ borderTop: '1px solid #eef2f7' }}>
                          <td style={cellStyle}>{frequency}</td>
                          <td style={cellStyle}>
                            <input
                              type="number"
                              min={0}
                              value={entry.amount}
                              onChange={(event) => handleFieldChange(frequency, 'amount', Number(event.target.value || 0))}
                              style={inputStyle}
                            />
                          </td>
                          <td style={cellStyle}>
                            <input
                              type="text"
                              value={entry.currency}
                              onChange={(event) => handleFieldChange(frequency, 'currency', event.target.value)}
                              style={inputStyle}
                            />
                          </td>
                          <td style={cellStyle}>
                            <input
                              type="text"
                              value={entry.stripePriceId ?? ''}
                              onChange={(event) => handleFieldChange(frequency, 'stripePriceId', event.target.value)}
                              style={{ ...inputStyle, minWidth: 220 }}
                            />
                          </td>
                          <td style={cellStyle}>
                            <input
                              type="number"
                              min={1}
                              value={entry.tierIncrement}
                              onChange={(event) => handleFieldChange(frequency, 'tierIncrement', Number(event.target.value || 1))}
                              style={inputStyle}
                            />
                          </td>
                          <td style={cellStyle}>
                            <input
                              type="checkbox"
                              checked={entry.isActive}
                              onChange={(event) => handleFieldChange(frequency, 'isActive', event.target.checked)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 16 }}>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  style={{
                    background: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 18px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save pricing'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const cellStyle: React.CSSProperties = {
  padding: '12px 14px',
  verticalAlign: 'top',
  color: '#1f2937',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 110,
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  background: '#fff',
};

export default ManageCoursePricing;
